// src/utils/incidentColumnDefs.ts
import { ColDef, ValueGetterParams } from "ag-grid-community";
import { IncidentData } from "./incidentTypes";
import config from "../../config";
import "ag-grid-enterprise";
import { formatDuration } from "../durationUtils";

import FavoriteCheckbox from "../../Components/FavoriteCheckbox/FavoriteCheckbox";
const extractImageSrcOrText = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;

  const img = div.querySelector("img");
  if (img && img.src) {
    return img.src; // Return the image URL
  }

  // Else return plain text
  return div.textContent || div.innerText || "";
};
const formatDateUTC3 = (date: Date | string | null): string => {
  if (!date) return "";

  const d = typeof date === "string"
    ? new Date(date.replace(" ", "T"))
    : date;

  return d.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
const isAdmin = localStorage.getItem("isAdmin") === "true";
const isSuperAdmin = localStorage.getItem("superadmin") === "true";

function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}
const getColumnDefs = (
  handleEdit: (data: IncidentData) => void,
  handleDelete: (ticketId: string) => void
): ColDef<IncidentData>[] => {
  //console.log("isAdminnnnnnnnn", isAdmin);

  const columns: (ColDef<IncidentData> | false)[] = [
    {
      field: "isFavorite",
      headerName: "Favorite",
      pinned: "left",
      width: 100,
      cellRenderer: (params: any) => (
        <FavoriteCheckbox
          value={params.value}
          data={params.data}
          node={params.node}
        />
      ),
      sortable: true,

      comparator: (a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1),
    },

    {
      field: "ticketId",
      headerName: "Ticket ID",
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    { field: "status", headerName: "Status" },
    {
      field: "affectedCustomer",
      headerName: "Customer",
    },
    {
      field: "affectedNetwork",
      headerName: "Network",
    },
    {
      field: "affectedAntenna",
      headerName: "Antenna",
    },

    { field: "affectedSite", headerName: "Site" },
    { field: "affectedLink", headerName: "Link" },
    { field: "asset", headerName: "Asset", filter: "agTextColumnFilter" },
    isAdmin && {
      field: "timeElapsed",
      headerName: "Time Elapsed",
      filter: "agNumberColumnFilter",
      cellRenderer: (params: any) => {
        const timeElapsed = params.data.timeElapsed || 0;

        let color = "green";
        let icon = "fa-check-circle";

        if (timeElapsed >= 100) {
          color = "red";
          icon = "fa-exclamation-circle";
        } else if (timeElapsed >= 25) {
          color = "orange";
          icon = "fa-exclamation-triangle";
        }

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: "bold",
            }}
          >
            <i className={`fa ${icon}`} style={{ color, marginLeft: "8px" }} />
            <span style={{ color }}>{timeElapsed}%</span>
          </div>
        );
      },
    },

    {
      field: "summary",
      headerName: "Summary",
      valueGetter: (params: ValueGetterParams<IncidentData>) =>
        stripHtml(params.data?.summary || ""),
      filter: "agTextColumnFilter",
    },
    {
      field: "description",
      headerName: "Description",
      valueGetter: (params: ValueGetterParams<IncidentData>) =>
        stripHtml(params.data?.description || ""),
      filter: "agTextColumnFilter",
    },
    {
      field: "solution",
      headerName: "Solution",
      valueGetter: (params: ValueGetterParams<IncidentData>) =>
        extractImageSrcOrText(params.data?.solution || ""),
      filter: "agTextColumnFilter",
    },
    {
      field: "incidentStartDatetime",
      headerName: "Incident Start Date/Time",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.incidentStartDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.incidentStartDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0; // dates are equal
        },
      },
    },
    {
      field: "incidentCloseDatetime",
      headerName: "Incident Close Date/Time",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.incidentCloseDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.incidentCloseDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0; // dates are equal
        },
      },
    },
    isAdmin && {
      field: "incidentResolutionTime",
      headerName: "Incident Resolution Time",
      filter: "agNumberColumnFilter",
      valueGetter: (params): number => {
        const val = params.data?.incidentResolutionTime;
        if (!val) return 0;

        // Match a number with optional minus and optional decimal point
        const match = val.match(/-?\d+(\.\d+)?/);
        const num = match ? parseFloat(match[0]) : 0;

        return isNaN(num) ? 0 : num;
      },
      valueFormatter: (params) => {
        return params.data?.incidentResolutionTime ?? "";
      },
    },
    !isAdmin && {
      field: "incidentResolutionTime",
      headerName: "Incident Resolution Time",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        const timeStr = params.data?.incidentResolutionTime;
        if (!timeStr) return "";

        const timeNs = Number(timeStr); // convert string to number
        if (isNaN(timeNs)) return "";

        if (timeNs > 10000) {
          // Convert nanoseconds to minutes
          const minutes = Math.floor(timeNs / 1_000_000_000 / 60);
          return `${minutes}m`;
        } else {
          // Small value, just show as-is with "m"
          return `${timeNs}m`;
        }
      },
    },
   {
      field: "totalPausedDurationSeconds",
      headerName: "Paused Time",
      filter: "agNumberColumnFilter",
      // Paused time in whole minutes (1h shows as 60m), so the number filter works in minutes too
      valueGetter: (params): number =>
        Math.floor((Number(params.data?.totalPausedDurationSeconds) || 0) / 60),
      valueFormatter: (params) => {
        // Under a minute: show the seconds instead of 0m
        const seconds = Number(params.data?.totalPausedDurationSeconds) || 0;
        if (seconds >= 1 && seconds < 60) return `${seconds}s`;
        return `${Number(params.value) || 0}m`;
      },
    },
    isAdmin && { field: "responsibleTeam", headerName: "Responsible Team" },
    isAdmin && { field: "responsiblePerson", headerName: "Responsible Person" },

    isAdmin && {
      field: "impact",
      headerName: "Impact",
      filter: "agSetColumnFilter",

      valueGetter: (params) => params.data?.impact,
      filterParams: {
        values: ["High", "Medium", "Low", "Critical"],
        suppressRemoveEntries: true,
      },
    },
    { field: "urgency", headerName: "Urgency" },
    isAdmin && { field: "priority", headerName: "Priority" },
    // {
    //   field: "userid",
    //   headerName: "User",
    //   filter: "agTextColumnFilter",
    // },
    {
      field: "user",
      headerName: "User",
      filter: "agTextColumnFilter",
    },
    {
      field: "createdby",
      headerName: "Created By",
    },
    {
      field: "closedby",
      headerName: "Closed By",
    },
    { field: "closureCode", headerName: "Closure Code" },
    { field: "errorType", headerName: "Error Type" },
    isAdmin && { field: "category", headerName: "Category" },
    {
      field: "ticketCreationDatetime",
      headerName: "Ticket Creation Date/Time",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.ticketCreationDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.ticketCreationDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0; // dates are equal
        },
      },
      sort: "desc",
      sortIndex: 3,
    },
    {
      field: "ticketClosureDatetime",
      headerName: "Ticket Closure Date/Time",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.ticketClosureDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.ticketClosureDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0; // dates are equal
        },
      },
    },
    isAdmin && {
      field: "responseTimeToOpen",
      headerName: "Response Time to Open",
      filter: "agNumberColumnFilter",
      valueGetter: (params): number => {
        const val = params.data?.responseTimeToOpen;
        if (!val) return 0;

        const match = val.match(/-?\d+(\.\d+)?/);
        const num = match ? parseFloat(match[0]) : 0;

        return isNaN(num) ? 0 : num;
      },
      valueFormatter: (params) => {
        return params.data?.responseTimeToOpen ?? "";
      },
    },
    isAdmin && {
      field: "responseTimeToClose",
      headerName: "Response Time to Close",
      filter: "agNumberColumnFilter",
      valueGetter: (params): number => {
        const val = params.data?.responseTimeToClose;
        if (!val) return 0;

        // Match a number with optional minus and optional decimal point
        const match = val.match(/-?\d+(\.\d+)?/);
        const num = match ? parseFloat(match[0]) : 0;

        return isNaN(num) ? 0 : num;
      },
      valueFormatter: (params) => {
        return params.data?.responseTimeToClose ?? "";
      },
    },

    isAdmin && {
      field: "reactionPoint",
      headerName: "Reaction Point",
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        return date
          .toLocaleString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          .replace(",", "");
      },
      hide: !isAdmin,
    },
    isAdmin && {
      field: "acceptanceTime",
      headerName: "Acceptance Time",
      filter: "agDateColumnFilter",

      valueFormatter: (params: any) => formatDateUTC3(params.value),
    },

    isAdmin && {
      field: "isaccepted",
      headerName: "is Accepted",
      cellRenderer: (params: any) => (
        <input type="checkbox" checked={!!params.value} disabled />
      ),
    },
    isAdmin && {
      field: "acceptanceStatus",
      headerName: "Acceptance Status",
      filter: "agTextColumnFilter",
    },
    isAdmin && { field: "sla", headerName: "SLA" },
    isAdmin && { field: "slaStatus", headerName: "SLA Status" },
    isAdmin && {
      field: "ola",
      headerName: "OLA MSO",
      filter: "agNumberColumnFilter",
      cellRenderer: (params: any) => {
        const ola = params.data.ola || 0;

        let color = "green";
        let icon = "fa-check-circle";

        if (ola >= 100) {
          color = "red";
          icon = "fa-exclamation-circle";
        } else if (ola >= 25) {
          color = "orange";
          icon = "fa-exclamation-triangle";
        }

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: "bold",
            }}
          >
            <i className={`fa ${icon}`} style={{ color, marginLeft: "8px" }} />
            <span style={{ color }}>{ola}%</span>
          </div>
        );
      },
    },
    isAdmin && { field: "olaStatus", headerName: "OLA MSO Status" },
    isAdmin && {
      field: "olai",
      headerName: "OLA COE/INCFS",
      cellRenderer: (params: any) => {
        const olai = params.data?.olai ?? 0;

        // 🔥 If OLAI = 0 → return an empty cell
        if (olai === 0) {
          return null;
        }

        let color = "green";
        let icon = "fa-check-circle";

        if (olai >= 100) {
          color = "red";
          icon = "fa-exclamation-circle";
        } else if (olai >= 25) {
          color = "orange";
          icon = "fa-exclamation-triangle";
        }

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: "bold",
            }}
          >
            <i className={`fa ${icon}`} style={{ color, marginLeft: "8px" }} />
            <span style={{ color }}>{olai}%</span>
          </div>
        );
      },
      filter: "agNumberColumnFilter",
    },

    isAdmin && { field: "olaistatus", headerName: "OLA COE/INCFS Status" },
    isAdmin && {
      field: "incidentCrossReference",
      headerName: "Incident Cross Reference",
    },

    isAdmin && {
      field: "relatedobject",
      headerName: "Related Object",
    },
    isAdmin && {
      field: "customerComplaint",
      headerName: "Customer Complaint",
      cellRenderer: (params: any) => (
        <input type="checkbox" checked={!!params.value} disabled />
      ),
    },

    isAdmin && {
      field: "customerInformed",
      headerName: "Customer Informed",
      cellRenderer: (params: any) => (
        <input type="checkbox" checked={!!params.value} disabled />
      ),
    },

    isSuperAdmin && {
      colId: "actions",
      headerName: "Actions",
      cellRenderer: (params: any) => (
        <div style={{ display: "flex", gap: "8px" }}>
          {
            <button onClick={() => handleDelete(params.data.ticketId)}>
              Delete
            </button>
          }
        </div>
      ),
      pinned: "right",
      flex: 1,
    },
  ];

  return columns.filter(Boolean) as ColDef<IncidentData>[];
};

export default getColumnDefs;
